import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  FormControlLabel,
  makeStyles,
  IconButton,
  Box,
  Paper,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { appUrl } from '../../../../../utils/config';
import { dateFormated, presentFormatedData } from '../functionalComponents/dataFormatFunctions';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      maxWidth: '1100px',
      width: '100%',
    },
  },
  dialogTitle: {
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
  },
  alertContainer: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '16px',
    margin: '16px 0',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  warningIcon: {
    color: '#ff6b35',
    fontSize: '24px',
    marginTop: '2px',
  },
  alertTitle: {
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#856404',
    marginBottom: '8px',
  },
  alertText: {
    color: '#856404',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  // New styles matching VarianceAlertDialog
  iconGrid: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  icon: {
    fontSize: '5rem',
    color: 'red',
  },
  alertTitleCentered: {
    textAlign: 'center',
    padding: '0.5rem',
    fontWeight: 500,
    fontSize: '1.5rem',
    color: '#000',
  },
  alertTextCentered: {
    textAlign: 'center',
    fontSize: '1rem',
    margin: '1rem',
    color: '#000',
    //fontWeight: 600,
  },
  closeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 1,
  },
  tableContainer: {
    margin: '16px 0',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    fontWeight: 'bold',
    fontSize: '14px',
    padding: '12px 8px',
    border: '1px solid #dee2e6',
  },
  tableCell: {
    fontSize: '14px',
    padding: '8px',
    border: '1px solid #dee2e6',
  },
  varianceAlert: {
    color: '#dc3545',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 0',
    marginTop: '16px',
  },
  rejectButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    padding: '8px 24px',
    borderRadius: '20px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#1976D2',
    },
  },
  approveButton: {
    backgroundColor: '#EC6535',
    color: 'white',
    padding: '8px 24px',
    borderRadius: '20px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#D55528',
    },
  },
  checkboxContainer: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '12px',
    margin: '16px 0',
  },
  detailsTable: {
    marginTop: '16px',
  },
  detailsTableHeader: {
    backgroundColor: '#e9ecef',
    fontWeight: 'bold',
    fontSize: '14px',
    padding: '8px',
    borderTop: '1px solid #dee2e6',
    borderBottom: '1px solid #dee2e6',
    borderLeft: 'none',
    borderRight: 'none',
    whiteSpace: 'nowrap',
  },
  detailsTableCell: {
    fontSize: '14px',
    fontWeight: '500',
    padding: '6px 8px',
    borderTop: '1px solid #dee2e6',
    borderBottom: '1px solid #dee2e6',
    borderLeft: 'none',
    borderRight: 'none',
  },
  // New styles for sponsor table matching ViewStructuredInformation
  sponsorTableContainer: {
    boxShadow: "none", // Remove border and shadow from the container
    // margin: '16px 0',
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    textAlign: "left",
    backgroundColor: "#E5F2F7",
    borderBottom: "none", // Remove bottom border
    padding: "16px", // Standard padding for consistency
  },
  headerCell: {
    fontWeight: "bold",
  },
  tableCell2: {
    textAlign: "left",
    backgroundColor: "#E5F2F7",
    borderBottom: "none", // Remove bottom border
    padding: "16px", // Standard padding for consistency
    paddingTop: "0px",
    fontSize: '14px',
  },
}));

const ApproveRejectConsumedBatch = ({ open, onClose, rowInfo, onRefresh, batchApprove = false, source = 'fgBatches' }) => {
  const classes = useStyles();
  const history = useHistory();
  const [allowResubmission, setAllowResubmission] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);
  const [batchDetailsData, setBatchDetailsData] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');



  // Fetch component allocation data when component opens
  useEffect(() => {
    if (open && rowInfo) {
      fetchBatchDetailsAndComponentAllocation();
    }
  }, [open, rowInfo]);

  const fetchBatchDetailsAndComponentAllocation = async () => {
    if (!rowInfo) {
      console.error('No rowInfo provided');
      return;
    }

    setLoading(true);
    setError(null);

    try {
     
      const batchDetails = await fetchBatchDetails();
     
      await fetchComponentAllocationData(batchDetails);
    } catch (err) {
      console.error('Error in fetchBatchDetailsAndComponentAllocation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchDetails = async () => {
    if (!rowInfo?.batchDetails?.schrockenDeliveryScheduleBatchID) {
      console.error('Missing schrockenDeliveryScheduleBatchID for batch details');
      return null;
    }

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDeliveryScheduleBatchDetails`;
      const payload = {
        schrockenDeliveryScheduleBatchID: rowInfo.batchDetails.schrockenDeliveryScheduleBatchID,
      };

      const response = await axios.post(url, payload);
      
      if (response.data && response.data.status) {
        setBatchDetailsData(response.data.data);
        return response.data.data;
      } else {
        console.error('Failed to fetch batch details:', response.data);
        return null;
      }
    } catch (err) {
      console.error('Error fetching batch details:', err);
      throw err;
    }
  };

  const fetchComponentAllocationData = async (batchDetails) => {
    if (!rowInfo) {
      console.error('No rowInfo provided');
      return;
    }

    try {
      let url = "";
      
      // Check if we have batch details with bomConsumptionAddedIndicator (same logic as LotAllocationTable)
      if (
        rowInfo?.batchDetails &&
        rowInfo?.batchDetails?.bomConsumptionAddedIndicator
      ) {
        // First API endpoint - requires schrockenDeliveryScheduleBatchID and batchSize
        if (!rowInfo?.batchDetails?.schrockenDeliveryScheduleBatchID) {
          console.error('Missing schrockenDeliveryScheduleBatchID:', rowInfo?.batchDetails?.schrockenDeliveryScheduleBatchID);
          setError('Missing schrockenDeliveryScheduleBatchID');
          return;
        }
        
        // Get batchSize from batchDetails (fetched from API) or fallback to rowInfo
        const batchSize = batchDetails?.batchSize || rowInfo?.batchDetails?.batchSize;
        
        url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationPlannedAndActualNewDirect/${rowInfo?.batchDetails?.schrockenDeliveryScheduleBatchID}/${batchSize}`;
      } else {
        // Second API endpoint - requires schrockenSLID
        if (!rowInfo?.scheduleLineDetails?.schrockenSLID) {
          console.error('Missing schrockenSLID:', rowInfo?.scheduleLineDetails?.schrockenSLID);
          setError('Missing schrockenSLID');
          return;
        }
        
        url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationForPOLineNewDirect/${rowInfo?.scheduleLineDetails?.schrockenSLID}`;
      }
      
      
      const response = await axios.get(url);

      
      setApiData(response.data);
      
    } catch (err) {
      console.error('API Error:', err);
      throw err;
    }
  };

  // Process API data for display
  const processApiData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map((item) => {
      // Merge planned and actual data similar to ViewRmPmLotTable
      const plannedDetails = item.plannedLotNumberAllocationDetails || [];
      const actualDetails = item.actualConsumptionDetails || [];
      
      // Create a comprehensive list of all lot data
      const allLots = [];
      
      // Add planned allocations
      plannedDetails.forEach(planned => {
        allLots.push({
          lotNumber: planned.lotNumber || 'N/A',
          netAvailableQuantity: planned.netAvailableQuantity || 0,
          expiryDate: planned.expiryDate || '',
          plannedAllocatedQuantity: planned.plannedAllocatedQuantity || 0,
          actualConsumedQuantity: 0, // Will be filled from actual data
          source: 'planned'
        });
      });
      
      // Add actual consumption data
      actualDetails.forEach(actual => {
        const existingLot = allLots.find(lot => lot.lotNumber === actual.componentBatchNumber);
        if (existingLot) {
          existingLot.actualConsumedQuantity = actual.BomComponentConsumedQuantity || 0;
          existingLot.netAvailableQuantity = actual.netAvailableQuantity || existingLot.netAvailableQuantity;
          existingLot.expiryDate = actual.expiryDate || existingLot.expiryDate;
        } else {
          // New lot found in actual data
          allLots.push({
            lotNumber: actual.componentBatchNumber || 'N/A',
            netAvailableQuantity: actual.netAvailableQuantity || 0,
            expiryDate: actual.expiryDate || '',
            plannedAllocatedQuantity: 0,
            actualConsumedQuantity: actual.BomComponentConsumedQuantity || 0,
            source: 'actual'
          });
        }
      });
      
      // Determine lot mismatch logic based on lotMismatch flag
      let lotMismatchDisplay = item.lotsMismatch ? 'YES' : 'NO';
      
      return {
        componentCode: item.componentCode || 'N/A',
        componentName: item.componentName || 'N/A',
        // bomQuantity: item.BOMQuantity || 0,
        bomQuantity: item.componentQuantity || 0,
        uom: item.uom || 'N/A',
        quantityVarianceValue: item.quantityVarianceValue,
        lotMismatch: item.lotsMismatch,
        lotMismatchDisplay: lotMismatchDisplay,
        quantityVariancePresent: item.quantityVariancePresent || false,
        lots: allLots
      };
    });
  };

  // Static data for demonstration - showing multiple rows design
  // const staticProcessedData = [
  //   {
  //     componentCode: 'MAT001',
  //     componentName: 'Material Alpha',
  //     bomQuantity: 100.000,
  //     uom: 'KG',
  //     quantityVarianceValue: 5.5,
  //     lotMismatch: true,
  //     lotMismatchDisplay: 'YES',
  //     quantityVariancePresent: true,
  //     lots: [
  //       {
  //         lotNumber: 'BATCH-001',
  //         netAvailableQuantity: 50.000,
  //         expiryDate: '2024-12-31',
  //         plannedAllocatedQuantity: 50.000,
  //         actualConsumedQuantity: 52.500,
  //         source: 'planned'
  //       },
  //       {
  //         lotNumber: 'BATCH-002',
  //         netAvailableQuantity: 60.000,
  //         expiryDate: '2025-01-15',
  //         plannedAllocatedQuantity: 50.000,
  //         actualConsumedQuantity: 52.500,
  //         source: 'planned'
  //       }
  //     ]
  //   },
  //   {
  //     componentCode: 'MAT002',
  //     componentName: 'Material Beta',
  //     bomQuantity: 75.500,
  //     uom: 'L',
  //     quantityVarianceValue: 0,
  //     lotMismatch: false,
  //     lotMismatchDisplay: 'NO',
  //     quantityVariancePresent: false,
  //     lots: [
  //       {
  //         lotNumber: 'BATCH-003',
  //         netAvailableQuantity: 80.000,
  //         expiryDate: '2024-11-30',
  //         plannedAllocatedQuantity: 75.500,
  //         actualConsumedQuantity: 75.500,
  //         source: 'planned'
  //       }
  //     ]
  //   },
  //   {
  //     componentCode: 'MAT003',
  //     componentName: 'Material Gamma',
  //     bomQuantity: 200.000,
  //     uom: 'KG',
  //     quantityVarianceValue: -2.3,
  //     lotMismatch: true,
  //     lotMismatchDisplay: 'YES',
  //     quantityVariancePresent: true,
  //     lots: [
  //       {
  //         lotNumber: 'BATCH-004',
  //         netAvailableQuantity: 100.000,
  //         expiryDate: '2025-02-28',
  //         plannedAllocatedQuantity: 100.000,
  //         actualConsumedQuantity: 95.400,
  //         source: 'planned'
  //       },
  //       {
  //         lotNumber: 'BATCH-005',
  //         netAvailableQuantity: 110.000,
  //         expiryDate: '2025-03-10',
  //         plannedAllocatedQuantity: 100.000,
  //         actualConsumedQuantity: 95.400,
  //         source: 'planned'
  //       },
  //       {
  //         lotNumber: 'BATCH-006',
  //         netAvailableQuantity: 50.000,
  //         expiryDate: '2025-01-20',
  //         plannedAllocatedQuantity: 0,
  //         actualConsumedQuantity: 9.200,
  //         source: 'actual'
  //       }
  //     ]
  //   }
  // ];

  // const processedData = staticProcessedData; // Use static data for demonstration
  const processedData = processApiData(apiData?.data); // Uncomment to use real API data

  // Calculate variance percentage
  const calculateVariance = (planned, actual) => {
    if (!planned || planned === 0) return actual > 0 ? 100 : 0;
    return Math.round(((actual - planned) / planned) * 100);
  };

  // Format date for display
  const formatDate = (dateString) => {
    return dateFormated(dateString, "DD-MMM-YYYY", "N/A");
  };

  const sponsorData = {
    sponsorName: rowInfo?.scheduleLineDetails?.sponsorName ,
    cmo: rowInfo?.scheduleLineDetails?.cmoName || rowInfo?.scheduleLineDetails?.vendorName ,
    productCode: rowInfo?.scheduleLineDetails?.fgMaterialCode,
    productDescription: rowInfo?.scheduleLineDetails?.fgMaterialName,
    batchNumber: rowInfo?.batchDetails?.cmoBatchNumber || rowInfo?.batchDetails?.batchNumber || rowInfo?.batchDetails?.vendorBatchNumber ,
    batchSize: rowInfo?.batchDetails?.batchSize ,
    uom: rowInfo?.scheduleLineDetails?.targetQuantityUOM ,
    poNumber: rowInfo?.scheduleLineDetails?.scheduleAgreementNumber ,
  };

  const handleRejectBatch = async () => {
    // Handle reject logic here
    await updateBatchStatus(false);
  };

  const handleApproveBatch = async () => {
    // Handle approve logic here
    await updateBatchStatus(true);
  };


  const updateBatchStatus = async (isApproved) => {
    if (!rowInfo) {
      console.error('No rowInfo provided for batch status update');
      return;
    }

    // Validate required fields
    const requiredFields = {
      scheduleAgreementNumber: rowInfo?.scheduleLineDetails?.scheduleAgreementNumber,
      vendorBatchNumber: rowInfo?.batchDetails?.vendorBatchNumber,
      vendorMosymphonyID: rowInfo?.scheduleLineDetails?.vendorMosymphonyID,
      brandOwnerMosymphonyID: rowInfo?.scheduleLineDetails?.brandOwnerMosymphonyID,
      schrockenDeliveryScheduleBatchID: rowInfo?.batchDetails?.schrockenDeliveryScheduleBatchID
    };


    const missingFields = Object.entries(requiredFields).filter(([key, value]) => !value);
    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(([key]) => key).join(', ');
      setError(`Missing required fields: ${missingFieldNames}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const statusID = isApproved ? 39 : 41;
      const statusDescription = isApproved ? 'Consumption Approved' : 'Batch Not Accepted';
      const statusDisplayName = isApproved ? 'Consumption Approved' : 'Batch Not Accepted';

      let quantityVarianceValue = 0;
      let lotMismatch = false;
      let cmoProductCode = ''
      let cmoProductDescription = ''

      
      if (apiData?.data && Array.isArray(apiData.data) && apiData.data.length > 0) {
        const firstComponent = apiData.data[0];
        quantityVarianceValue = firstComponent?.quantityVarianceValue;
        lotMismatch = firstComponent?.lotsMismatch;
        cmoProductCode = apiData?.cmoProductCode;
        cmoProductDescription = apiData?.cmoProductDescription;
      }
    
      const cmoBatchNumber = batchDetailsData?.cmoBatchNumber;
     

      const payload = {
        sponsorName: rowInfo?.scheduleLineDetails?.sponsorName,
        BMSproductCode: sponsorData.productCode,
        BMSproductDesc: sponsorData.productDescription,
        CMOproductCode: batchApprove ? cmoProductCode : rowInfo?.scheduleLineDetails?.cmoProductCode,
        CMOproductDesc: batchApprove ? cmoProductDescription : rowInfo?.scheduleLineDetails?.cmoProductDescription,
        scheduleAgreementNumber: requiredFields.scheduleAgreementNumber,
        vendorBatchNumber: requiredFields.vendorBatchNumber,
        vendorMosymphonyID: requiredFields.vendorMosymphonyID,
        cmoBatchNumber: cmoBatchNumber,
        brandOwnerMosymphonyID: requiredFields.brandOwnerMosymphonyID,
        QtyVariance: quantityVarianceValue,
        LotMismatch: lotMismatch,
        batchDetailsList: [{
          updateFromProcessFlow: true,
          updateStatusFromProcessFlow: true,
          statusID: statusID,
          statusDescription: statusDescription,
          SASLBatchStatusID: statusID,
          statusDisplayName: statusDisplayName,
          schrockenDeliveryScheduleBatchID: requiredFields.schrockenDeliveryScheduleBatchID
        }]
      };

    

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDeliveryScheduleBatch`;
      
      const response = await axios.post(url, payload);
      
      
      if (response.data && response.data.status) {

        const successMessage = isApproved 
          ? 'Batch has been successfully approved.' 
          : 'Batch has been successfully rejected.';
        setDialogMessage(successMessage);
        setShowSuccessDialog(true);
      } else {
        setDialogMessage('Failed to update batch status. Please try again.');
        setShowErrorDialog(true);
      }
      
    } catch (err) {
      console.error('Error updating batch status:', err);
      
      setDialogMessage(err.message || 'An error occurred while updating batch status. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    
    if (onRefresh) {
      onRefresh('batchStatusUpdate');
    }
    
    onClose();
    
    const { collabType, roleName, organizationName, ecosystemId } = localStorage;
    const path = source === 'poDashboard'
      ? `/pcmp/${collabType}/${roleName}/${organizationName}/${ecosystemId}/purchaseorderManagement`
      : `/pcmp/${collabType}/${roleName}/${organizationName}/${ecosystemId}/fgBatches`;
    window.location.href = path;
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    
    onClose();
    
    const { collabType, roleName, organizationName, ecosystemId } = localStorage;
    const path = source === 'poDashboard'
      ? `/pcmp/${collabType}/${roleName}/${organizationName}/${ecosystemId}/purchaseorderManagement`
      : `/pcmp/${collabType}/${roleName}/${organizationName}/${ecosystemId}/fgBatches`;
    window.location.href = path;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        className={classes.dialog}
      >
        <DialogContent>
        {/* Close Button */}
        <IconButton onClick={onClose} className={classes.closeButton} size="small">
          <CloseIcon />
        </IconButton>
        
        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: '16px' }}>
              Loading component allocation data...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography variant="body1" color="error">
              Error loading data: {error}
            </Typography>
          </Box>
        )}

        {/* Main Content - Show when not loading and no error */}
        {!loading && !error && (
          <>
            {/* Alert Section */}
            <Grid container>
              <Grid item xs={12} className={classes.iconGrid}>
                <WarningIcon className={classes.icon} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h5' className={classes.alertTitleCentered}>
                  Batch Mismatch, Quantity Variance Alert
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' className={classes.alertTextCentered}>
                  The consumed batch has materials that have crossed the allowed variance limit and/or 
                  there is a batch mismatch. Please approve if you still want to proceed with the next steps.
                </Typography>
              </Grid>
            </Grid>


            {/* Sponsor Details Table */}
            <Grid
              container
              justifyContent="flex-start"
              style={{ backgroundColor: "#c2e1ef" }}
            >
              <Grid item>
                <TableContainer
                  component={Paper}
                  className={classes.sponsorTableContainer}
                >
                  <Table className={classes.table} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          Sponsor Name
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          CMO
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          Product Code / Description
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          Batch Number
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          Batch Size
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          UoM
                        </TableCell>
                        <TableCell
                          className={`${classes.tableCell} ${classes.headerCell}`}
                        >
                          PO Number
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.sponsorName || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.cmo || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(
                            `${sponsorData.productCode} / ${sponsorData.productDescription}` ||
                            ""
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.batchNumber || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.batchSize || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.uom || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell2}>
                          {presentFormatedData(sponsorData.poNumber || "")}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>

        {/* Material Details Table */}
        <TableContainer component={Paper} className={classes.detailsTable}>
          <Table size="small" style={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.detailsTableHeader} rowSpan={2}>
                  <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                    BMS Material Code
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '2px' }}>
                    / Description
                  </div>
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  BoM Quantity
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  UOM
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  Batch Numbers
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  Planned Allocated Qty
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  Actual Consumed Qty
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  Quantity Variance (%)
                </TableCell>
                <TableCell className={classes.detailsTableHeader} rowSpan={2} align="center" style={{ textAlign: 'center' }}>
                  Batches Mismatch
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No component allocation data available
                  </TableCell>
                </TableRow>
              ) : (
                processedData.map((material, materialIndex) => {
                  const maxLots = Math.max(1, material.lots.length);
                  return material.lots.length > 0 ? (
                    material.lots.map((lot, lotIndex) => (
                      <TableRow key={`${materialIndex}-${lotIndex}`}>
                        {/* Main component columns - only render in first row with rowSpan */}
                        {lotIndex === 0 && (
                          <>
                            <TableCell 
                              className={classes.detailsTableCell} 
                              rowSpan={maxLots}
                              style={{ 
                                borderLeft: 'none',
                                borderRight: 'none',
                                verticalAlign: 'top',
                                paddingTop: '12px'
                              }}
                            >
                              <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {material.componentCode} /
                              </div>
                              <div style={{ fontWeight: '500', lineHeight: '1.4' }}>
                                {material.componentName}
                              </div>
                            </TableCell>
                            <TableCell 
                              className={classes.detailsTableCell} 
                              rowSpan={maxLots}
                              align="right"
                              style={{ 
                                borderLeft: 'none',
                                borderRight: 'none',
                                verticalAlign: 'middle',
                                textAlign: 'right'
                              }}
                            >
                              {material.bomQuantity !== undefined && material.bomQuantity !== null
                                ? Number(material.bomQuantity).toFixed(3)
                                : ''}
                            </TableCell>
                            <TableCell 
                              className={classes.detailsTableCell} 
                              rowSpan={maxLots}
                              align="left"
                              style={{ 
                                borderLeft: 'none',
                                borderRight: 'none',
                                verticalAlign: 'middle',
                                textAlign: 'left'
                              }}
                            >
                              {material.uom}
                            </TableCell>
                          </>
                        )}
                        
                        {/* Lot-specific columns - render for each lot row */}
                        <TableCell 
                          className={classes.detailsTableCell}
                          align="left"
                          style={{ 
                            borderLeft: 'none',
                            borderRight: 'none',
                            textAlign: 'left'
                          }}
                        >
                          {lot.lotNumber || 'N/A'}
                        </TableCell>
                        <TableCell 
                          className={classes.detailsTableCell} 
                          align="right"
                          style={{ 
                            borderLeft: 'none',
                            borderRight: 'none',
                            textAlign: 'right',
                            paddingRight: '8px'
                          }}
                        >
                          <div style={{ textAlign: 'right', width: '100%' }}>
                            {lot.plannedAllocatedQuantity !== undefined && lot.plannedAllocatedQuantity !== null
                              ? Number(lot.plannedAllocatedQuantity).toFixed(3)
                              : '0.000'}
                          </div>
                        </TableCell>
                        <TableCell 
                          className={classes.detailsTableCell} 
                          align="right"
                          style={{ 
                            borderLeft: 'none',
                            borderRight: 'none',
                            textAlign: 'right',
                            paddingRight: '8px'
                          }}
                        >
                          <div style={{ textAlign: 'right', width: '100%' }}>
                            {lot.actualConsumedQuantity !== undefined && lot.actualConsumedQuantity !== null
                              ? Number(lot.actualConsumedQuantity).toFixed(3)
                              : '0.000'}
                          </div>
                        </TableCell>
                        
                        {/* Variance and Mismatch columns - only render in first row with rowSpan */}
                        {lotIndex === 0 && (
                          <>
                            <TableCell 
                              className={classes.detailsTableCell} 
                              rowSpan={maxLots}
                              align="right"
                              style={{ 
                                borderLeft: 'none',
                                borderRight: 'none',
                                verticalAlign: 'middle',
                                textAlign: 'right'
                              }}
                            >
                              {(() => {
                                const variance = material.quantityVarianceValue;
                                if (variance === "NA" || variance === null || variance === undefined) {
                                  return <span></span>;
                                }
                                if (variance === 0) {
                                  return '0';
                                }
                                const displayValue = variance % 1 !== 0 ? Number(variance).toFixed(2) : variance;
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                    <span>
                                      {displayValue}
                                    </span>
                                    {(material?.quantityVariancePresent) && (
                                      <WarningIcon fontSize='small' color='error' />
                                    )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell 
                              className={classes.detailsTableCell} 
                              rowSpan={maxLots}
                              align="center"
                              style={{ 
                                borderLeft: 'none',
                                borderRight: 'none',
                                verticalAlign: 'middle',
                                textAlign: 'center'
                              }}
                            >
                              {material.lotMismatchDisplay === 'NA' ? (
                                ''
                              ) : material.lotMismatchDisplay === 'YES' ? (
                                <span className={classes.varianceAlert}>
                                  YES
                                </span>
                              ) : 'NO'}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow key={materialIndex}>
                      <TableCell className={classes.detailsTableCell}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {material.componentCode}
                        </div>
                        <div style={{ fontWeight: '500', lineHeight: '1.4' }}>
                          {material.componentName}
                        </div>
                      </TableCell>
                      <TableCell className={classes.detailsTableCell} align="right">
                        {material.bomQuantity !== undefined && material.bomQuantity !== null
                          ? Number(material.bomQuantity).toFixed(3)
                          : ''}
                      </TableCell>
                      <TableCell className={classes.detailsTableCell}>
                        {material.uom}
                      </TableCell>
                      <TableCell className={classes.detailsTableCell}>N/A</TableCell>
                      <TableCell className={classes.detailsTableCell} align="right">N/A</TableCell>
                      <TableCell className={classes.detailsTableCell} align="right">N/A</TableCell>
                      <TableCell className={classes.detailsTableCell} align="right">N/A</TableCell>
                      <TableCell className={classes.detailsTableCell} align="center">
                        {material.lotMismatchDisplay === 'NA' ? (
                          ''
                        ) : material.lotMismatchDisplay === 'YES' ? (
                          <span className={classes.varianceAlert}>
                            YES
                          </span>
                        ) : 'NO'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Allow Resubmission Checkbox */}
        <div className={classes.checkboxContainer}>
          <FormControlLabel
            control={
              <Checkbox
                // checked={allowResubmission}
                checked={false}
                disabled={true} // Disable checkbox as per requirement
                onChange={(e) => setAllowResubmission(e.target.checked)}
                color="primary"
              />
            }
            label="Allow for Resubmission"
          />
        </div>

            {/* Action Buttons */}
            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                className={classes.rejectButton}
                onClick={handleRejectBatch}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} /> : null}
                Reject Consumed Batch
              </Button>
              <Button
                variant="contained"
                className={classes.approveButton}
                onClick={handleApproveBatch}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} /> : null}
                Approve Consumed Batch
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Success Dialog */}
    <Dialog
      open={showSuccessDialog}
      onClose={handleSuccessDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent style={{ padding: '40px', textAlign: 'center' }}>
        <CheckCircleIcon style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
          Success!
        </Typography>
        <Typography variant="body1">
          {dialogMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', paddingBottom: '24px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSuccessDialogClose}
          style={{ minWidth: '120px' }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>

    {/* Error Dialog */}
    <Dialog
      open={showErrorDialog}
      onClose={handleErrorDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent style={{ padding: '40px', textAlign: 'center' }}>
        <ErrorIcon style={{ fontSize: 60, color: '#f44336', marginBottom: '16px' }} />
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
          Error
        </Typography>
        <Typography variant="body1">
          {dialogMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', paddingBottom: '24px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleErrorDialogClose}
          style={{ minWidth: '120px' }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default ApproveRejectConsumedBatch;
